package com.schoolconnect.app.feature.imports

import androidx.compose.ui.graphics.Color
import com.schoolconnect.app.datasource.network.UserSessionStore
import com.schoolconnect.app.datasource.network.response.BulkUploadResponse
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import com.schoolconnect.app.util.MAX_WORKBOOK_BYTES
import com.schoolconnect.app.feature.common.pageCountForTotal
import com.schoolconnect.app.repository.imports.BulkImportRepository
import com.schoolconnect.app.repository.user.UserRepository
import com.schoolconnect.app.designsystem.AppToastCenter

class ImportsViewModel(
    private val schoolCode: String = "",
    private val schoolId: String = "",
    private val currentUserId: String = UserSessionStore.getUserId()
) {
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private val _uiState = MutableStateFlow(ImportsUiState())
    private var selectedWorkbookFileName: String = ""
    private var selectedWorkbookBytes: ByteArray? = null

    val uiState: StateFlow<ImportsUiState> = _uiState.asStateFlow()

    fun onEvent(event: ImportsUiEvent) {
        when (event) {
            ImportsUiEvent.BackClicked -> Unit
            ImportsUiEvent.BackToValidationSummaryClicked -> {
                _uiState.update { state ->
                    state.copy(
                        stage = ImportWorkflowStage.ValidationSummary,
                        steps = validationSteps(),
                        isLoading = false
                    )
                }
            }
            ImportsUiEvent.DownloadCurrentDataClicked -> downloadWorkbook(includeSchoolCode = true)
            ImportsUiEvent.DownloadTemplateClicked -> downloadWorkbook(includeSchoolCode = false)
            ImportsUiEvent.UploadFileClicked -> Unit
            ImportsUiEvent.ValidateFileClicked -> {
                validateWorkbook()
            }
            ImportsUiEvent.DownloadErrorDetailsClicked -> downloadErrorDetails()
            ImportsUiEvent.DismissStructureErrors -> _uiState.update { it.copy(structureValidationErrors = emptyList()) }
            ImportsUiEvent.FilterClicked -> Unit
            ImportsUiEvent.CancelClicked -> {
                selectedWorkbookFileName = ""
                selectedWorkbookBytes = null
                _uiState.value = ImportsUiState()
            }
            ImportsUiEvent.ContinueReviewClicked -> {
                _uiState.update { state ->
                    if (state.errorRows.isNotEmpty()) {
                        state.copy(showReviewWarning = true, errorMessage = "")
                    } else if (!state.serverValidationPassed) {
                        state.copy(errorMessage = "Validate the workbook before continuing.")
                    } else {
                        state.copy(
                            stage = ImportWorkflowStage.ReviewAndSave,
                            steps = reviewSteps(),
                            errorMessage = ""
                        )
                    }
                }
            }
            ImportsUiEvent.DismissReviewWarning -> _uiState.update { it.copy(showReviewWarning = false) }
            ImportsUiEvent.ConfirmContinueReviewClicked -> {
                _uiState.update {
                    it.copy(
                        showReviewWarning = false,
                        stage = ImportWorkflowStage.ReviewAndSave,
                        steps = reviewSteps(),
                        errorMessage = ""
                    )
                }
            }
            ImportsUiEvent.DismissSaveWarning -> _uiState.update { it.copy(showSaveWarning = false) }
            ImportsUiEvent.ConfirmSaveRecordsClicked -> {
                _uiState.update { it.copy(showSaveWarning = false, errorMessage = "") }
                uploadWorkbook()
            }
            ImportsUiEvent.SaveRecordsClicked -> {
                val state = _uiState.value
                if (!state.serverValidationCompleted) {
                    _uiState.update { it.copy(errorMessage = "Validate the workbook before saving records.") }
                } else if (state.errorRows.isNotEmpty()) {
                    _uiState.update { it.copy(showSaveWarning = true, errorMessage = "") }
                } else if (state.serverValidationPassed && state.errorMessage.isBlank()) {
                    uploadWorkbook()
                } else {
                    _uiState.update { it.copy(errorMessage = "Validate the workbook before saving records.") }
                }
            }
            is ImportsUiEvent.WorkbookPrepared -> {
                if (event.fileBytes.size > MAX_WORKBOOK_BYTES) {
                    selectedWorkbookFileName = ""
                    selectedWorkbookBytes = null
                    _uiState.update { it.copy(errorMessage = "Workbook is too large. Maximum size is 25 MB.") }
                } else {
                    selectedWorkbookFileName = event.fileName
                    selectedWorkbookBytes = event.fileBytes
                    _uiState.update {
                        it.copy(
                            serverValidationCompleted = false,
                            serverValidationPassed = false,
                            showReviewWarning = false,
                            showSaveWarning = false
                        )
                    }
                }
            }
            is ImportsUiEvent.WorkbookLoadStarted -> {
                _uiState.update { state ->
                    state.copy(
                        isLoading = true,
                        errorMessage = "",
                        reviewSummary = state.reviewSummary.copy(
                            fileName = event.fileName,
                            uploadedOn = "Reading file...",
                            readyToSaveRows = 0,
                            status = "Validating"
                        )
                    )
                }
            }
            is ImportsUiEvent.WorkbookLoaded -> applyWorkbookPreview(event.preview)
            is ImportsUiEvent.WorkbookLoadFailed -> {
                selectedWorkbookFileName = ""
                selectedWorkbookBytes = null
                _uiState.update { state ->
                    state.copy(
                        isLoading = false,
                        errorMessage = event.message
                    )
                }
            }
            is ImportsUiEvent.TabSelected -> _uiState.update { state ->
                state.copy(
                    selectedTabId = event.tabId,
                    reviewPageIndex = state.reviewPageIndexByTab[event.tabId] ?: 0,
                    reviewTabs = state.reviewTabs.map { tab ->
                        tab.copy(isSelected = tab.id.equals(event.tabId, ignoreCase = true))
                    }
                )
            }
            is ImportsUiEvent.RowsPerPageChanged -> _uiState.update { state ->
                state.copy(rowsPerPage = event.rowsPerPage.coerceAtLeast(1))
            }
            is ImportsUiEvent.SheetSelected -> _uiState.update { state ->
                state.copy(
                    selectedSheet = event.sheetName,
                    pageIndex = 0
                )
            }
            is ImportsUiEvent.PageChanged -> _uiState.update { state ->
                when (state.stage) {
                    ImportWorkflowStage.ValidationSummary -> state.copy(pageIndex = event.pageIndex.coerceAtLeast(0))
                    ImportWorkflowStage.ReviewAndSave -> {
                        val tabId = state.selectedTabId
                        val updatedPageIndexes = state.reviewPageIndexByTab.toMutableMap()
                        updatedPageIndexes[tabId] = event.pageIndex.coerceAtLeast(0)
                        state.copy(
                            reviewPageIndexByTab = updatedPageIndexes,
                            reviewPageIndex = event.pageIndex.coerceAtLeast(0)
                        )
                    }
                }
            }
        }
    }

    fun clear() {
        scope.cancel()
    }

    private fun downloadWorkbook(includeSchoolCode: Boolean) {
        if (_uiState.value.isLoading) return
        _uiState.update { it.copy(isLoading = true, errorMessage = "") }
        scope.launch {
            delay(300)
            val result = if (includeSchoolCode) {
                resolveSchoolCode().fold(
                    onSuccess = { resolvedCode -> BulkImportRepository.downloadSchoolSetupTemplate(resolvedCode) },
                    onFailure = { throwable -> Result.failure(throwable) }
                )
            } else {
                BulkImportRepository.downloadSchoolSetupTemplate()
            }
            result.fold(
                onSuccess = {
                    _uiState.update { it.copy(isLoading = false) }
                },
                onFailure = { throwable ->
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            errorMessage = throwable.message ?: "Unable to download the school setup workbook."
                        )
                    }
                }
            )
        }
    }

    private fun validateWorkbook() {
        if (_uiState.value.isLoading) return
        val fileBytes = selectedWorkbookBytes
        if (fileBytes == null || fileBytes.isEmpty()) {
            _uiState.update { it.copy(errorMessage = "Upload an XLSX workbook before validating.") }
            return
        }
        val fileName = selectedWorkbookFileName.ifBlank { _uiState.value.reviewSummary.fileName }
        _uiState.update { state ->
            state.copy(
                isLoading = true,
                serverValidationCompleted = false,
                serverValidationPassed = false,
                errorMessage = "",
                validationLastUpdated = "Validating with server...",
                validationDetails = state.validationDetails.map { row ->
                    if (row.label == "Status") row.copy(badgeText = "Validating", badgeColor = Color(0xFFB45309)) else row
                }
            )
        }
        scope.launch {
            val result = resolveSchoolCode().fold(
                onSuccess = { resolvedCode ->
                    BulkImportRepository.validateSchoolSetup(
                        schoolCode = resolvedCode,
                        fileName = fileName,
                        bytes = fileBytes,
                        currentUserId = currentUserId
                    )
                },
                onFailure = { throwable -> Result.failure(throwable) }
            )
            result.fold(
                onSuccess = { response -> applyServerValidation(response.validationErrors.orEmpty(), response.message.orEmpty()) },
                onFailure = { throwable ->
                    _uiState.update { state ->
                        state.copy(
                            isLoading = false,
                            serverValidationCompleted = false,
                            serverValidationPassed = false,
                            errorMessage = throwable.message ?: "Failed to validate school setup workbook.",
                            validationLastUpdated = "Validation failed",
                            validationDetails = state.validationDetails.map { row ->
                                if (row.label == "Status") row.copy(badgeText = "Validation Failed", badgeColor = Color(0xFFDC2626)) else row
                            }
                        )
                    }
                }
            )
        }
    }

    private fun applyServerValidation(validationErrors: List<String>, message: String) {
        val serverErrorRows = validationErrors
            .filter { it.isNotBlank() }
            .mapIndexed { index, error ->
                ImportErrorRow(sheetName = "Server/API", rowNo = "—", message = error, id = "server-api:$index")
            }
        val totalRows = _uiState.value.staffRecords.size +
            _uiState.value.subjectRecords.size +
            _uiState.value.classRecords.size +
            _uiState.value.assignmentRecords.size
        val errorCount = serverErrorRows.size
        val errorPageCount = pageCountForTotal(errorCount, _uiState.value.rowsPerPage)
        val passed = serverErrorRows.isEmpty()
        _uiState.update { state ->
            state.copy(
                stage = if (passed) ImportWorkflowStage.ReviewAndSave else ImportWorkflowStage.ValidationSummary,
                steps = if (passed) reviewSteps() else validationSteps(),
                isLoading = false,
                serverValidationCompleted = true,
                serverValidationPassed = passed,
                errorMessage = "",
                errorRows = serverErrorRows,
                selectedSheet = "All Sheets",
                sheetOptions = listOf("All Sheets", "Staff", "Subjects", "Classes", "Teaching Assignments", "Reference Values", "Server/API"),
                totalErrorsLabel = if (passed) "Validation Passed" else "Validation Issues ($errorCount)",
                errorHelpText = if (passed) {
                    "Server validation passed. Review the records before saving."
                } else {
                    "The server found issues in this workbook. Resolve them and validate again."
                },
                errorPageText = buildErrorPageText(errorCount, errorPageCount),
                pageIndex = 0,
                pageCount = errorPageCount,
                validationLastUpdated = message.ifBlank { "Server validation complete" },
                validationDetails = listOf(
                    ImportSummaryRow("Total Rows", totalRows.toString()),
                    ImportSummaryRow("Valid Rows", if (passed) totalRows.toString() else "—", valueColor = Color(0xFF16A34A)),
                    ImportSummaryRow("Validation Errors", errorCount.toString(), valueColor = Color(0xFFDC2626)),
                    ImportSummaryRow(
                        "Status",
                        badgeText = if (passed) "Validated" else "Errors Found",
                        badgeBackground = if (passed) Color(0xFFE8F8EF) else Color(0xFFFFECEF),
                        badgeColor = if (passed) Color(0xFF16A34A) else Color(0xFFDC2626)
                    )
                ),
                reviewSummary = state.reviewSummary.copy(
                    readyToSaveRows = if (passed) totalRows else 0,
                    status = if (passed) "Ready to Save" else "Review Required"
                )
            )
        }
    }

    private fun downloadErrorDetails() {
        val state = _uiState.value
        val rows = state.errorRows.filter { row ->
            state.selectedSheet.equals("All Sheets", ignoreCase = true) ||
                row.sheetName.equals(state.selectedSheet, ignoreCase = true)
        }
        if (rows.isEmpty()) {
            _uiState.update { it.copy(errorMessage = "There are no validation errors to download.") }
            return
        }
        if (state.isLoading) return
        _uiState.update { it.copy(isLoading = true, errorMessage = "") }
        scope.launch {
            downloadValidationErrorWorkbook("School_Setup_Error_Details.xlsx", rows).fold(
                onSuccess = { _uiState.update { it.copy(isLoading = false) } },
                onFailure = { throwable ->
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            errorMessage = throwable.message ?: "Unable to download validation error details."
                        )
                    }
                }
            )
        }
    }

    private fun applyWorkbookPreview(preview: ImportsWorkbookPreview) {
        if (preview.structureErrors.isNotEmpty()) {
            selectedWorkbookFileName = ""
            selectedWorkbookBytes = null
            _uiState.update { state ->
                state.copy(
                    isLoading = false,
                    structureValidationErrors = preview.structureErrors,
                    errorRows = emptyList(),
                    staffRecords = emptyList(),
                    subjectRecords = emptyList(),
                    classRecords = emptyList(),
                    assignmentRecords = emptyList(),
                    totalErrorsLabel = "Workbook Structure Error",
                    errorHelpText = "Download and fill in the School Setup template, keeping its tabs and headers unchanged.",
                    errorMessage = ""
                )
            }
            return
        }
        val totalRows = preview.staffRecords.size + preview.subjectRecords.size + preview.classRecords.size + preview.assignmentRecords.size
        val reviewTabs = listOf(
            ImportTabUiModel(
                id = "staff",
                title = "Staff",
                count = preview.staffRecords.size,
                isSelected = true
            ),
            ImportTabUiModel(
                id = "subjects",
                title = "Subjects",
                count = preview.subjectRecords.size
            ),
            ImportTabUiModel(
                id = "classes",
                title = "Classes",
                count = preview.classRecords.size
            ),
            ImportTabUiModel(
                id = "teaching-assignments",
                title = "Teaching Assignments",
                count = preview.assignmentRecords.size
            )
        )
        val errorPageCount = pageCountForTotal(0, _uiState.value.rowsPerPage)
        val statusLabel = "Ready to Validate"
        val statusBackground = Color(0xFFFFF4D6)
        val statusColor = Color(0xFFB45309)

        _uiState.update { state ->
            state.copy(
                stage = ImportWorkflowStage.ValidationSummary,
                steps = validationSteps(),
                existingDetails = listOf(
                    ImportSummaryRow("Staff", preview.staffRecords.size.toString()),
                    ImportSummaryRow("Subjects", preview.subjectRecords.size.toString()),
                    ImportSummaryRow("Classes", preview.classRecords.size.toString()),
                    ImportSummaryRow("Teaching Assignments", preview.assignmentRecords.size.toString())
                ),
                newImportDetails = listOf(
                    ImportSummaryRow("File Name", preview.fileName),
                    ImportSummaryRow("Selected On", preview.uploadedOn),
                    ImportSummaryRow("Selected By", "School Admin"),
                    ImportSummaryRow("Total Rows", totalRows.toString())
                ),
                validationDetails = listOf(
                    ImportSummaryRow("Total Rows", totalRows.toString()),
                    ImportSummaryRow("Valid Rows", "—", valueColor = Color(0xFF16A34A)),
                    ImportSummaryRow("Validation Errors", "—", valueColor = Color(0xFFDC2626)),
                    ImportSummaryRow(
                        "Status",
                        badgeText = statusLabel,
                        badgeBackground = statusBackground,
                        badgeColor = statusColor
                    )
                ),
                validationLastUpdated = "Just now",
                sheetOptions = listOf("All Sheets", "Staff", "Subjects", "Classes", "Teaching Assignments", "Reference Values"),
                selectedSheet = "All Sheets",
                totalErrorsLabel = "Validation Pending",
                errorHelpText = "Select Validate File to check workbook data with the server.",
                errorRows = preview.errorRows,
                structureValidationErrors = emptyList(),
                errorPageText = buildErrorPageText(preview.errorRows.size, errorPageCount),
                pageIndex = 0,
                pageCount = errorPageCount,
                reviewSummary = ImportReviewSummary(
                    fileName = preview.fileName,
                    uploadedOn = preview.uploadedOn,
                    readyToSaveRows = totalRows,
                    status = "Validation Required"
                ),
                reviewTabs = reviewTabs,
                selectedTabId = reviewTabs.firstOrNull()?.id.orEmpty().ifBlank { "staff" },
                staffRecords = preview.staffRecords,
                subjectRecords = preview.subjectRecords,
                classRecords = preview.classRecords,
                assignmentRecords = preview.assignmentRecords,
                reviewPageIndex = 0,
                reviewPageIndexByTab = reviewTabs.associate { it.id to 0 },
                isLoading = false,
                errorMessage = preview.validationMessages.firstOrNull().orEmpty()
            )
        }
    }

    private fun uploadWorkbook() {
        val fileBytes = selectedWorkbookBytes
        if (fileBytes == null || fileBytes.isEmpty()) {
            _uiState.update { it.copy(errorMessage = "Upload an XLSX workbook before saving records.") }
            return
        }
        val fileName = selectedWorkbookFileName.ifBlank { _uiState.value.reviewSummary.fileName }
        scope.launch {
            _uiState.update { state ->
                state.copy(
                    isLoading = true,
                    errorMessage = "",
                    reviewSummary = state.reviewSummary.copy(status = "Uploading...")
                )
            }
            val result = resolveSchoolCode().fold(
                onSuccess = { resolvedCode ->
                    BulkImportRepository.uploadSchoolSetup(
                        schoolCode = resolvedCode,
                        fileName = fileName,
                        bytes = fileBytes,
                        currentUserId = currentUserId
                    )
                },
                onFailure = { throwable -> Result.failure(throwable) }
            )
            result.fold(
                onSuccess = { response -> handleUploadSuccess(response) },
                onFailure = { throwable ->
                    _uiState.update { state ->
                        state.copy(
                            isLoading = false,
                            errorMessage = throwable.message ?: "Failed to upload school setup workbook.",
                            reviewSummary = state.reviewSummary.copy(status = "Upload Failed")
                        )
                    }
                }
            )
        }
    }

    private suspend fun resolveSchoolCode(): Result<String> {
        val directCode = schoolCode.trim()
            .ifBlank { UserSessionStore.getHomeRoleSchoolCode().trim() }
        if (directCode.isNotBlank()) return Result.success(directCode)

        val targetSchoolId = schoolId.trim()
            .ifBlank { UserSessionStore.getHomeRoleSchoolId().trim() }
        return UserRepository.loadUserRoleAuthorizations(currentUserId).mapCatching { response ->
            val authorization = response.authorizations.orEmpty().firstOrNull { item ->
                targetSchoolId.isBlank() || item.tenant?.schoolId.orEmpty().trim() == targetSchoolId
            } ?: throw IllegalStateException("Unable to resolve the active school authorization.")
            authorization.tenant?.schoolCode.orEmpty().trim().takeIf { it.isNotBlank() }
                ?: throw IllegalStateException("The active school authorization has no school code.")
        }
    }

    private fun handleUploadSuccess(response: BulkUploadResponse) {
        val uploadedRows = listOf(
            response.staffCreated,
            response.subjectsCreated,
            response.classesCreated,
            response.allocationsCreated
        ).sumOf { it ?: 0 }
        val failedRows = listOf(
            response.staffFailed,
            response.subjectsFailed,
            response.classesFailed,
            response.allocationsFailed
        ).sumOf { it ?: 0 }
        val isSuccess = response.success == true
        val responseMessage = response.message.orEmpty()
        val summaryStatus = responseMessage.ifBlank {
            if (isSuccess) "Upload Complete" else "Upload Failed"
        }
        if (isSuccess) {
            AppToastCenter.showSuccess("School setup imported successfully")
            selectedWorkbookFileName = ""
            selectedWorkbookBytes = null
            _uiState.value = ImportsUiState()
            return
        }
        _uiState.update { state ->
            state.copy(
                isLoading = false,
                errorMessage = responseMessage.ifBlank { "School setup upload failed." },
                reviewSummary = state.reviewSummary.copy(
                    readyToSaveRows = uploadedRows,
                    status = summaryStatus
                ),
                validationDetails = listOf(
                    ImportSummaryRow("Total Rows", (uploadedRows + failedRows).toString()),
                    ImportSummaryRow("Valid Rows", uploadedRows.toString(), valueColor = Color(0xFF16A34A)),
                    ImportSummaryRow("Invalid Rows", failedRows.toString(), valueColor = Color(0xFFDC2626)),
                    ImportSummaryRow(
                        "Status",
                        badgeText = summaryStatus,
                        badgeBackground = if (isSuccess) Color(0xFFE8F8EF) else Color(0xFFFFECEF),
                        badgeColor = if (isSuccess) Color(0xFF16A34A) else Color(0xFFDC2626)
                    )
                ),
                validationLastUpdated = "Just now"
            )
        }
    }

    private fun validationSteps(): List<ImportStepUiModel> = listOf(
        ImportStepUiModel(number = 1, title = "Step 1", subtitle = "Upload & Validate", isActive = true),
        ImportStepUiModel(number = 2, title = "Step 2", subtitle = "Review & Save")
    )

    private fun reviewSteps(): List<ImportStepUiModel> = listOf(
        ImportStepUiModel(number = 1, title = "Step 1", subtitle = "Upload & Validate", isCompleted = true),
        ImportStepUiModel(number = 2, title = "Step 2", subtitle = "Review & Save", isActive = true)
    )

    private fun buildErrorPageText(totalErrors: Int, pageCount: Int): String {
        if (totalErrors <= 0) return "Showing 0 to 0 of 0 errors"
        val safePageCount = pageCount.coerceAtLeast(1)
        val pageSize = ((totalErrors + safePageCount - 1) / safePageCount).coerceAtLeast(1)
        val end = minOf(pageSize, totalErrors)
        return "Showing 1 to $end of $totalErrors errors"
    }
}
